import clsx from "clsx";
import { User, Route } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { CaretPosition } from "./caretCoordinates";
import type { MentionCandidate, MentionGroup } from "./mentions";

type MentionMenuProps = {
  id: string;
  query: string;
  groups: MentionGroup[];
  placement?: "caret" | "above";
  /** The groups flattened, which is the order the arrow keys walk. */
  candidates: MentionCandidate[];
  /** Where the "@" sits inside the textarea. */
  anchor: CaretPosition;
  activeIndex: number;
  onHover: (index: number) => void;
  onPick: (candidate: MentionCandidate) => void;
};

/**
 * The list an "@" opens, pinned just under the line the token is on so it reads
 * as belonging to the token rather than to the whole box.
 */
const MentionMenu = ({
  id,
  query,
  groups,
  placement = "caret",
  candidates,
  anchor,
  activeIndex,
  onHover,
  onPick,
}: MentionMenuProps) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <ul
      ref={listRef}
      id={id}
      role="listbox"
      aria-label={t("chat_scope_label")}
      style={
        placement === "above"
          ? { left: 0, bottom: "calc(100% + 8px)", width: "100%" }
          : { left: anchor.left, top: anchor.top + anchor.lineHeight + 4 }
      }
      className="absolute z-20 max-h-[min(18rem,45vh)] w-64 overflow-y-auto rounded-xl border border-outline bg-surface py-1 shadow-xl"
    >
      {candidates.length === 0 && (
        <li className="px-3 py-2 text-sm text-text-dim">
          {t("no_results_for")} @{query}
        </li>
      )}
      {groups.map((group, groupIndex) => (
        <React.Fragment key={group.kind}>
          {/* Only worth heading a group when there is another one to tell it from. */}
          {groups.length > 1 && (
            <li
              className={clsx(
                "px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-text-dim",
                groupIndex === 0 ? "pt-1" : "mt-1 border-t border-outline pt-2",
              )}
            >
              {group.kind === "person" ? t("people") : t("processes")}
            </li>
          )}
          {group.options.map(candidate => {
            const index = candidates.indexOf(candidate);
            return (
              <li role="presentation" key={`${candidate.kind}-${candidate.id}`}>
                {/* Keep focus in the textarea until the option's click is handled. */}
                <button
                  type="button"
                  role="option"
                  tabIndex={-1}
                  id={`${id}-${index}`}
                  aria-selected={index === activeIndex}
                  onMouseDown={event => {
                    event.preventDefault();
                  }}
                  onClick={() => onPick(candidate)}
                  onMouseEnter={() => onHover(index)}
                  className={clsx(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-text transition",
                    index === activeIndex && "bg-muted-surface",
                  )}
                >
                  {candidate.kind === "person" ? (
                    <User size={14} className="shrink-0 text-text-dim" />
                  ) : (
                    <Route size={14} className="shrink-0 text-info" />
                  )}
                  <span className="min-w-0 truncate">{candidate.label}</span>
                </button>
              </li>
            );
          })}
        </React.Fragment>
      ))}
    </ul>
  );
};

export default MentionMenu;
